// /producto/:id — SSR head() built from the products table.
import { createFileRoute, notFound } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/lib/route-boundaries";
import { getHost } from "@/lib/host";
import ProductDetail from "@/pages/ProductDetail";
import { supabase } from "@/integrations/supabase/client";
import {
  canonicalOriginFromHost,
  localeFromHost,
  type LocaleKey,
} from "@/lib/locale-from-host";
import { buildHreflangLinks } from "@/lib/hreflang";

function pickName(locale: LocaleKey, row: { name: string | null; name_es: string | null }) {
  if (locale === "es") return row.name_es ?? row.name ?? "Producto";
  return row.name ?? row.name_es ?? "Producto";
}

function pickDescription(
  locale: LocaleKey,
  row: { description: string | null; description_es: string | null }
): string {
  const raw = locale === "es" ? row.description_es ?? row.description : row.description ?? row.description_es;
  if (!raw) return "";
  const plain = raw.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (!plain) return "";
  return plain.length > 160 ? `${plain.slice(0, 157).trimEnd()}…` : plain;
}

const PRODUCT_BRAND = " | Fora da Rota";
const PRODUCT_MAX_TITLE = 60;

function buildProductTitle(name: string): string {
  const full = `${name}${PRODUCT_BRAND}`;
  if (full.length <= PRODUCT_MAX_TITLE) return full;
  const budget = PRODUCT_MAX_TITLE - PRODUCT_BRAND.length - 1;
  return `${name.slice(0, Math.max(1, budget)).trimEnd()}…${PRODUCT_BRAND}`;
}

function buildProductDescription(locale: LocaleKey, name: string, base: string): string {
  const fallback =
    locale === "es"
      ? `Reserva ${name} con Fora da Rota: mejores precios, disponibilidad en tiempo real y atención personalizada para planear tu viaje.`
      : locale === "pt-BR"
        ? `Reserve ${name} com a Fora da Rota: melhores preços, disponibilidade em tempo real e atendimento personalizado para sua viagem.`
        : `Book ${name} with Fora da Rota: best rates, real-time availability and personalized service to plan your trip with confidence.`;
  let text = base && base.length >= 50 ? base : fallback;
  if (text.length > 160) text = `${text.slice(0, 157).trimEnd()}…`;
  return text;
}


export const Route = createFileRoute("/producto/$id")({
  loader: async ({ params }) => {
    const host = getHost();
    const locale = localeFromHost(host);
    const origin = canonicalOriginFromHost(host);

    const { data, error } = await supabase
      .from("products")
      .select("id,name,name_es,description,description_es,images,product_type,is_active")
      .eq("id", params.id)
      .maybeSingle();

    if (error || !data) throw notFound();

    const images = Array.isArray(data.images)
      ? (data.images.filter((i) => typeof i === "string") as string[])
      : [];

    return {
      product: {
        name: pickName(locale, data),
        description: pickDescription(locale, data),
        image: images[0] ?? null,
        productType: data.product_type ?? null,
        active: data.is_active !== false,
      },
      locale,
      origin,
    };
  },
  head: ({ params, loaderData }) => {
    const origin = loaderData?.origin ?? "https://www.foradarota.com";
    const url = `${origin}/producto/${params.id}`;

    if (!loaderData) {
      return {
        meta: [
          { title: "Producto | Fora da Rota" },
          { name: "robots", content: "noindex" },
        ],
      };
    }

    const { product, locale } = loaderData;
    const title = buildProductTitle(product.name);
    const description = buildProductDescription(locale, product.name, product.description);
    const image = product.image ?? `${origin}/og-image.jpg`;


    return {
      meta: [
        { title },
        { name: "description", content: description },
        ...(product.active ? [] : [{ name: "robots", content: "noindex" }]),
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      links: [
        { rel: "canonical", href: url },
        ...buildHreflangLinks(`/producto/${params.id}`),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            description,
            image,
            url,
          }),
        },
      ],
    };
  },
  component: ProductDetail,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: () => <RouteNotFoundBoundary message="Este producto no está disponible." />,
});
